<template>
  <div class="coupon-management">
    <div class="page-header">
      <div>
        <h2 class="page-title">优惠券管理</h2>
        <p class="page-desc">创建和管理优惠券活动，提升用户转化</p>
      </div>
      <el-button type="primary" size="large" @click="openAddDialog">
        <el-icon>
          <Plus />
        </el-icon>
        新建优惠券
      </el-button>
    </div>

    <!-- 统计卡片 -->
    <div class="stat-cards">
      <div class="stat-card">
        <div class="stat-value">{{ stats.total || 0 }}</div>
        <div class="stat-label">优惠券种类</div>
      </div>
      <div class="stat-card success">
        <div class="stat-value">{{ stats.total_quantity || 0 }}</div>
        <div class="stat-label">发行总量</div>
      </div>
      <div class="stat-card warning">
        <div class="stat-value">{{ stats.total_received || 0 }}</div>
        <div class="stat-label">已领取</div>
      </div>
      <div class="stat-card primary">
        <div class="stat-value">{{ stats.total_used || 0 }}</div>
        <div class="stat-label">已使用</div>
      </div>
    </div>

    <!-- 搜索筛选栏 -->
    <div class="filter-bar">
      <el-input v-model="searchKey" placeholder="搜索优惠券名称/编号" prefix-icon="Search" clearable style="width: 260px"
        @keyup.enter="handleSearch" />
      <el-select v-model="couponTypeFilter" placeholder="全部类型" clearable style="width: 120px" @change="handleSearch">
        <el-option label="满减券" :value="1" />
        <el-option label="折扣券" :value="2" />
        <el-option label="代金券" :value="3" />
        <el-option label="兑换券" :value="4" />
      </el-select>
      <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px" @change="handleSearch">
        <el-option label="启用" :value="1" />
        <el-option label="禁用" :value="0" />
      </el-select>
      <el-button type="primary" @click="handleSearch">查询</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </div>

    <!-- 优惠券列表 -->
    <div class="coupon-list">
      <div class="coupon-card" v-for="coupon in couponList" :key="coupon.id">
        <div class="coupon-left" :class="getCouponClass(coupon.coupon_type)">
          <div class="coupon-value">
            <span v-if="coupon.coupon_type === 1 || coupon.coupon_type === 3">¥</span>
            <span class="value-number">{{ formatCouponValue(coupon) }}</span>
            <span v-if="coupon.coupon_type === 2">折</span>
          </div>
          <div class="coupon-name">{{ coupon.coupon_name }}</div>
        </div>
        <div class="coupon-right">
          <div class="coupon-info">
            <div class="coupon-condition" v-if="coupon.min_amount > 0">
              满¥{{ coupon.min_amount }}可用
            </div>
            <div class="coupon-condition" v-else>无门槛</div>
            <div class="coupon-stock">
              剩余 {{ (coupon.total_quantity || 0) - (coupon.received_quantity || 0) }} / {{ coupon.total_quantity || 0 }}
            </div>
            <div class="coupon-date">
              <span v-if="coupon.valid_days">领取后{{ coupon.valid_days }}天内有效</span>
              <span v-else-if="coupon.valid_start_date && coupon.valid_end_date">
                {{ coupon.valid_start_date }} ~ {{ coupon.valid_end_date }}
              </span>
              <span v-else>长期有效</span>
            </div>
          </div>
          <div class="coupon-actions">
            <el-tag :type="coupon.status === 1 ? 'success' : 'danger'" size="small">
              {{ coupon.status === 1 ? '启用' : '禁用' }}
            </el-tag>
            <el-button type="primary" link size="small" @click="openEditDialog(coupon)">
              <el-icon>
                <Edit />
              </el-icon> 编辑
            </el-button>
            <el-button type="success" link size="small" @click="openSendDialog(coupon)">
              <el-icon>
                <Share />
              </el-icon> 发放
            </el-button>
            <el-button type="primary" link size="small" @click="viewUserCoupons(coupon)">
              <el-icon>
                <User />
              </el-icon> 领取记录
            </el-button>
            <el-popconfirm title="确定删除该优惠券吗？" @confirm="handleDelete(coupon.id)">
              <template #reference>
                <el-button type="danger" link size="small">
                  <el-icon>
                    <Delete />
                  </el-icon> 删除
                </el-button>
              </template>
            </el-popconfirm>
          </div>
        </div>
      </div>

      <div v-if="couponList.length === 0 && !loading" class="empty-state">
        <el-empty description="暂无优惠券，点击右上角新建" />
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper" v-if="pagination.total > pagination.limit">
      <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.limit"
        :total="pagination.total" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next"
        @size-change="fetchData" @current-change="fetchData" />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog v-model="dialogVisible" :title="isEditMode ? '编辑优惠券' : '新建优惠券'" width="700px" destroy-on-close>
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="110px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="优惠券名称" prop="coupon_name" required>
              <el-input v-model="formData.coupon_name" placeholder="请输入优惠券名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="优惠券类型" prop="coupon_type" required>
              <el-select v-model="formData.coupon_type" placeholder="请选择类型" style="width: 100%">
                <el-option label="满减券" :value="1" />
                <el-option label="折扣券" :value="2" />
                <el-option label="代金券" :value="3" />
                <el-option label="兑换券" :value="4" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="优惠值" prop="discount_value" required>
              <el-input-number v-model="formData.discount_value" :min="0"
                :precision="formData.coupon_type === 2 ? 2 : 0" :step="formData.coupon_type === 2 ? 0.01 : 1"
                :max="formData.coupon_type === 2 ? 10 : 9999" style="width: 100%">
                <template #append v-if="formData.coupon_type === 2">折</template>
                <template #append v-else>元</template>
              </el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="使用门槛">
              <el-input-number v-model="formData.min_amount" :min="0" :precision="0" style="width: 100%">
                <template #append>元</template>
              </el-input-number>
              <div class="form-tip">0表示无门槛</div>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="发行总量">
              <el-input-number v-model="formData.total_quantity" :min="0" :step="100" style="width: 100%" />
              <div class="form-tip">0表示不限量</div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="每人限领">
              <el-input-number v-model="formData.user_limit" :min="1" :max="10" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="有效期">
          <el-radio-group v-model="validType" @change="handleValidTypeChange">
            <el-radio :label="1">固定日期</el-radio>
            <el-radio :label="2">领取后N天内有效</el-radio>
            <el-radio :label="3">长期有效</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item v-if="validType === 1" label="有效日期">
          <el-date-picker v-model="dateRange" type="daterange" range-separator="至" start-placeholder="开始日期"
            end-placeholder="结束日期" value-format="YYYY-MM-DD" style="width: 100%" />
        </el-form-item>

        <el-form-item v-if="validType === 2" label="有效天数">
          <el-input-number v-model="formData.valid_days" :min="1" :max="365" />
          <span class="form-tip">天</span>
        </el-form-item>

        <el-form-item label="优惠券说明">
          <el-input v-model="formData.description" type="textarea" :rows="3" placeholder="请输入优惠券使用说明" />
        </el-form-item>

        <el-form-item label="排序">
          <el-input-number v-model="formData.sort_order" :min="0" :max="999" />
        </el-form-item>

        <el-form-item label="状态">
          <el-switch v-model="formData.status" :active-value="1" :inactive-value="0" active-text="启用"
            inactive-text="禁用" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">确 定</el-button>
      </template>
    </el-dialog>

    <!-- 发放优惠券弹窗 -->
    <el-dialog v-model="sendDialogVisible" title="发放优惠券" width="600px">
      <div class="send-info">
        <div class="coupon-preview">
          <div class="preview-left" :class="getCouponClass(currentCoupon?.coupon_type)">
            <div class="preview-value">
              <span v-if="currentCoupon?.coupon_type === 1 || currentCoupon?.coupon_type === 3">¥</span>
              <span v-if="currentCoupon?.coupon_type === 2">{{ (currentCoupon?.discount_value * 10).toFixed(0) }}</span>
              <span class="value-number">{{ formatCouponValue(currentCoupon) }}</span>
              <span v-if="currentCoupon?.coupon_type === 2">折</span>
            </div>
            <div class="preview-name">{{ currentCoupon?.coupon_name }}</div>
          </div>
          <div class="preview-info">
            <div>满{{ currentCoupon?.min_amount || 0 }}元可用</div>
            <div>剩余 {{ (currentCoupon?.total_quantity || 0) - (currentCoupon?.received_quantity || 0) }}张</div>
          </div>
        </div>

        <el-form :model="sendForm" label-width="80px">
          <el-form-item label="选择用户" required>
            <el-select v-model="sendForm.userIds" multiple filterable remote reserve-keyword placeholder="请输入手机号或昵称搜索"
              :remote-method="searchUsers" :loading="userSearchLoading" style="width: 100%">
              <el-option v-for="user in userOptions" :key="user.id"
                :label="`${user.nickname || '用户'} (${user.phone || '未绑定'})`" :value="user.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="发放数量">
            <span>每人{{ currentCoupon?.user_limit || 1 }}张</span>
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <el-button @click="sendDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSend" :loading="sendLoading">确认发放</el-button>
      </template>
    </el-dialog>

    <!-- 领取记录弹窗 -->
    <el-dialog v-model="recordDialogVisible" :title="`${currentCoupon?.coupon_name} - 领取记录`" width="800px">
      <el-table :data="userCoupons" v-loading="recordLoading" border>
        <el-table-column label="用户" min-width="150">
          <template #default="{ row }">
            <div class="user-info">
              <el-avatar :size="32" :src="row.avatar_url || defaultAvatar" />
              <span>{{ row.nickname || '匿名用户' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="phone" label="手机号" width="120" />
        <el-table-column label="优惠券码" width="150">
          <template #default="{ row }">
            <span class="coupon-code">{{ row.coupon_no }}</span>
            <el-button link size="small" @click="copyCode(row.coupon_no)">
              <el-icon>
                <CopyDocument />
              </el-icon>
            </el-button>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="80">
          <template #default="{ row }">
            <el-tag :type="getUserCouponStatusType(row.status)" size="small">
              {{ getUserCouponStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="receive_time" label="领取时间" width="160" />
        <el-table-column prop="use_time" label="使用时间" width="160">
          <template #default="{ row }">{{ row.use_time || '--' }}</template>
        </el-table-column>
      </el-table>

      <div class="pagination-small" v-if="recordPagination.total > recordPagination.limit">
        <el-pagination v-model:current-page="recordPagination.page" v-model:page-size="recordPagination.limit"
          :total="recordPagination.total" layout="prev, pager, next" small @current-change="loadUserCoupons" />
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { Plus, Edit, Delete, Share, User, CopyDocument } from '@element-plus/icons-vue'

export default {
  name: 'CouponManagementPage',
  components: { Plus, Edit, Delete, Share, User, CopyDocument },
  setup() {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const agentId = ref(userInfo.agent_id || 0)
    const storeId = ref(userInfo.id)

    const loading = ref(false)
    const submitting = ref(false)
    const searchKey = ref('')
    const couponTypeFilter = ref(null)
    const statusFilter = ref(null)
    const couponList = ref([])
    const stats = ref({})
    const defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'

    const dialogVisible = ref(false)
    const isEditMode = ref(false)
    const formRef = ref(null)
    const validType = ref(3)
    const dateRange = ref([])

    const formData = reactive({
      id: null,
      coupon_name: '',
      coupon_type: 1,
      discount_value: 10,
      min_amount: 0,
      total_quantity: 0,
      user_limit: 1,
      valid_days: null,
      valid_start_date: null,
      valid_end_date: null,
      description: '',
      status: 1,
      sort_order: 0
    })

    const formRules = {
      coupon_name: [{ required: true, message: '请输入优惠券名称', trigger: 'blur' }],
      coupon_type: [{ required: true, message: '请选择优惠券类型', trigger: 'change' }],
      discount_value: [{ required: true, message: '请输入优惠值', trigger: 'blur' }]
    }

    // 发放相关
    const sendDialogVisible = ref(false)
    const sendLoading = ref(false)
    const currentCoupon = ref(null)
    const sendForm = reactive({ userIds: [] })
    const userOptions = ref([])
    const userSearchLoading = ref(false)

    // 领取记录相关
    const recordDialogVisible = ref(false)
    const recordLoading = ref(false)
    const userCoupons = ref([])
    const recordPagination = reactive({ page: 1, limit: 10, total: 0 })

    const pagination = reactive({
      page: 1,
      limit: 10,
      total: 0
    })

    // 格式化优惠券值
    const formatCouponValue = (coupon) => {
      if (!coupon) return ''
      if (coupon.coupon_type === 1 || coupon.coupon_type === 3) {
        return coupon.discount_value
      } else if (coupon.coupon_type === 2) {
        return (coupon.discount_value * 10).toFixed(0)
      } else if (coupon.coupon_type === 4) {
        return '兑换券'
      }
      return coupon.discount_value
    }

    // 获取优惠券样式类
    const getCouponClass = (type) => {
      const map = { 1: 'type-full', 2: 'type-discount', 3: 'type-cash', 4: 'type-exchange' }
      return map[type] || 'type-full'
    }

    // 获取用户优惠券状态文本
    const getUserCouponStatusText = (status) => {
      const map = { 0: '未使用', 1: '已使用', 2: '已过期', 3: '已失效' }
      return map[status] || '未知'
    }

    const getUserCouponStatusType = (status) => {
      const map = { 0: 'info', 1: 'success', 2: 'danger', 3: 'danger' }
      return map[status] || 'info'
    }

    // 复制
    const copyCode = async (code) => {
      try {
        await navigator.clipboard.writeText(code)
        ElMessage.success('已复制')
      } catch {
        ElMessage.error('复制失败')
      }
    }

    // 处理有效期类型变化
    const handleValidTypeChange = (type) => {
      if (type === 1) {
        formData.valid_days = null
      } else if (type === 2) {
        formData.valid_start_date = null
        formData.valid_end_date = null
        formData.valid_days = 30
      } else {
        formData.valid_start_date = null
        formData.valid_end_date = null
        formData.valid_days = null
      }
    }

    // 获取优惠券列表
    const fetchData = async () => {
      loading.value = true
      try {
        const params = {
          action: 'list',
          agent_id: agentId.value,
          store_id: storeId.value,
          page: pagination.page,
          limit: pagination.limit,
          search: searchKey.value,
          coupon_type: couponTypeFilter.value,
          status: statusFilter.value
        }

        const response = await axios.get(`${config.BASE_URL}/shop/api/coupon_api.php`, { params })

        if (response.data.code === 200) {
          couponList.value = response.data.data.list
          stats.value = response.data.data.stats
          pagination.total = response.data.data.pagination.total
        }
      } catch (error) {
        console.error('获取优惠券列表失败:', error)
        ElMessage.error('获取数据失败')
      } finally {
        loading.value = false
      }
    }

    // 搜索用户
    const searchUsers = async (query) => {
      if (!query) {
        userOptions.value = []
        return
      }

      userSearchLoading.value = true
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/member_api.php`, {
          params: {
            action: 'list',
            agent_id: agentId.value,
            store_id: storeId.value,
            search: query,
            limit: 20
          }
        })
        if (response.data.code === 200) {
          userOptions.value = response.data.data.list
        }
      } catch (error) {
        console.error('搜索用户失败:', error)
      } finally {
        userSearchLoading.value = false
      }
    }

    // 加载用户优惠券
    const loadUserCoupons = async () => {
      if (!currentCoupon.value) return

      recordLoading.value = true
      try {
        const response = await axios.get(`${config.BASE_URL}/shop/api/coupon_api.php`, {
          params: {
            action: 'user_coupons',
            agent_id: agentId.value,
            store_id: storeId.value,
            coupon_id: currentCoupon.value.id,
            page: recordPagination.page,
            limit: recordPagination.limit
          }
        })

        if (response.data.code === 200) {
          userCoupons.value = response.data.data.list
          recordPagination.total = response.data.data.pagination.total
        }
      } catch (error) {
        console.error('加载用户优惠券失败:', error)
      } finally {
        recordLoading.value = false
      }
    }

    // 搜索
    const handleSearch = () => {
      pagination.page = 1
      fetchData()
    }

    // 重置搜索
    const resetSearch = () => {
      searchKey.value = ''
      couponTypeFilter.value = null
      statusFilter.value = null
      pagination.page = 1
      fetchData()
    }

    // 打开新增弹窗
    const openAddDialog = () => {
      isEditMode.value = false
      validType.value = 3
      dateRange.value = []
      Object.assign(formData, {
        id: null,
        coupon_name: '',
        coupon_type: 1,
        discount_value: 10,
        min_amount: 0,
        total_quantity: 0,
        user_limit: 1,
        valid_days: null,
        valid_start_date: null,
        valid_end_date: null,
        description: '',
        status: 1,
        sort_order: 0
      })
      dialogVisible.value = true
    }

    // 打开编辑弹窗
    const openEditDialog = (coupon) => {
      isEditMode.value = true
      Object.assign(formData, coupon)

      if (coupon.valid_start_date && coupon.valid_end_date) {
        validType.value = 1
        dateRange.value = [coupon.valid_start_date, coupon.valid_end_date]
      } else if (coupon.valid_days) {
        validType.value = 2
      } else {
        validType.value = 3
      }

      dialogVisible.value = true
    }

    // 提交表单
    const submitForm = async () => {
      if (!formRef.value) return

      await formRef.value.validate(async (valid) => {
        if (!valid) return

        submitting.value = true
        const loadingInstance = ElLoading.service({ text: '保存中...' })

        try {
          const payload = {
            agent_id: agentId.value,
            store_id: storeId.value,
            ...formData
          }

          if (validType.value === 1 && dateRange.value && dateRange.value.length === 2) {
            payload.valid_start_date = dateRange.value[0]
            payload.valid_end_date = dateRange.value[1]
            payload.valid_days = null
          } else if (validType.value === 2) {
            payload.valid_start_date = null
            payload.valid_end_date = null
          } else {
            payload.valid_start_date = null
            payload.valid_end_date = null
            payload.valid_days = null
          }

          delete payload.id

          let response
          if (isEditMode.value) {
            payload.id = formData.id
            response = await axios.put(`${config.BASE_URL}/shop/api/coupon_api.php`, payload)
          } else {
            response = await axios.post(`${config.BASE_URL}/shop/api/coupon_api.php`, payload)
          }

          if (response.data.code === 200) {
            ElMessage.success(isEditMode.value ? '更新成功' : '创建成功')
            dialogVisible.value = false
            fetchData()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('提交失败:', error)
          ElMessage.error('操作失败')
        } finally {
          submitting.value = false
          loadingInstance.close()
        }
      })
    }

    // 打开发放弹窗
    const openSendDialog = (coupon) => {
      currentCoupon.value = coupon
      sendForm.userIds = []
      userOptions.value = []
      sendDialogVisible.value = true
    }

    // 确认发放
    const confirmSend = async () => {
      if (sendForm.userIds.length === 0) {
        ElMessage.warning('请选择要发放的用户')
        return
      }

      sendLoading.value = true
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/coupon_api.php`, {
          action: 'send',
          agent_id: agentId.value,
          store_id: storeId.value,
          coupon_id: currentCoupon.value.id,
          user_ids: sendForm.userIds
        })

        if (response.data.code === 200) {
          ElMessage.success(response.data.message)
          sendDialogVisible.value = false
          fetchData()
        } else {
          ElMessage.error(response.data.message || '发放失败')
        }
      } catch (error) {
        console.error('发放失败:', error)
        ElMessage.error('发放失败')
      } finally {
        sendLoading.value = false
      }
    }

    // 查看用户优惠券
    const viewUserCoupons = async (coupon) => {
      currentCoupon.value = coupon
      recordPagination.page = 1
      recordDialogVisible.value = true
      await loadUserCoupons()
    }

    // 删除优惠券
    const handleDelete = async (id) => {
      const loadingInstance = ElLoading.service({ text: '删除中...' })
      try {
        const response = await axios.delete(`${config.BASE_URL}/shop/api/coupon_api.php`, {
          data: { id, store_id: storeId.value }
        })
        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          fetchData()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        ElMessage.error('删除失败')
      } finally {
        loadingInstance.close()
      }
    }

    onMounted(() => {
      fetchData()
    })

    return {
      loading,
      submitting,
      searchKey,
      couponTypeFilter,
      statusFilter,
      couponList,
      stats,
      pagination,
      dialogVisible,
      isEditMode,
      formRef,
      formData,
      formRules,
      validType,
      dateRange,
      sendDialogVisible,
      sendLoading,
      currentCoupon,
      sendForm,
      userOptions,
      userSearchLoading,
      recordDialogVisible,
      recordLoading,
      userCoupons,
      recordPagination,
      defaultAvatar,
      formatCouponValue,
      getCouponClass,
      getUserCouponStatusText,
      getUserCouponStatusType,
      copyCode,
      handleValidTypeChange,
      handleSearch,
      resetSearch,
      openAddDialog,
      openEditDialog,
      submitForm,
      openSendDialog,
      confirmSend,
      viewUserCoupons,
      handleDelete,
      searchUsers,
      loadUserCoupons,
      fetchData
    }
  }
}
</script>

<style scoped lang="scss">
.coupon-management {
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;

    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #1a1a2e;
      margin-bottom: 4px;
    }

    .page-desc {
      font-size: 14px;
      color: #8c8c8c;
    }
  }

  .stat-cards {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    flex-wrap: wrap;

    .stat-card {
      flex: 1;
      min-width: 120px;
      background: #fff;
      border-radius: 12px;
      padding: 16px 20px;
      text-align: center;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

      &.success .stat-value {
        color: #67c23a;
      }

      &.warning .stat-value {
        color: #e6a23c;
      }

      &.primary .stat-value {
        color: #409eff;
      }

      .stat-value {
        font-size: 28px;
        font-weight: 700;
      }

      .stat-label {
        font-size: 13px;
        color: #8c8c8c;
        margin-top: 4px;
      }
    }
  }

  .filter-bar {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 24px;
  }

  .coupon-list {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .coupon-card {
      background: #fff;
      border-radius: 16px;
      display: flex;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      transition: all 0.3s;

      &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      }

      .coupon-left {
        width: 140px;
        padding: 20px;
        text-align: center;
        color: #fff;

        &.type-full {
          background: linear-gradient(135deg, #f56c6c, #f78989);
        }

        &.type-discount {
          background: linear-gradient(135deg, #e6a23c, #ebb563);
        }

        &.type-cash {
          background: linear-gradient(135deg, #67c23a, #85ce61);
        }

        &.type-exchange {
          background: linear-gradient(135deg, #909399, #b3b6bb);
        }

        .coupon-value {
          font-size: 14px;

          .value-number {
            font-size: 36px;
            font-weight: 700;
          }
        }

        .coupon-name {
          font-size: 12px;
          margin-top: 8px;
          opacity: 0.9;
        }
      }

      .coupon-right {
        flex: 1;
        padding: 16px 20px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .coupon-info {
          .coupon-condition {
            font-size: 14px;
            color: #666;
            margin-bottom: 8px;
          }

          .coupon-stock {
            font-size: 13px;
            color: #f56c6c;
            margin-bottom: 8px;
          }

          .coupon-date {
            font-size: 12px;
            color: #8c8c8c;
          }
        }

        .coupon-actions {
          display: flex;
          gap: 12px;
          align-items: center;
        }
      }
    }
  }

  .empty-state {
    padding: 60px 0;
  }

  .pagination-wrapper {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }

  .form-tip {
    font-size: 12px;
    color: #8c8c8c;
    margin-top: 4px;
  }

  .send-info {
    .coupon-preview {
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 16px;
      background: #f5f5f5;
      border-radius: 12px;
      margin-bottom: 24px;

      .preview-left {
        width: 120px;
        padding: 16px;
        text-align: center;
        border-radius: 12px;
        color: #fff;

        &.type-full {
          background: linear-gradient(135deg, #f56c6c, #f78989);
        }

        &.type-discount {
          background: linear-gradient(135deg, #e6a23c, #ebb563);
        }

        &.type-cash {
          background: linear-gradient(135deg, #67c23a, #85ce61);
        }

        &.type-exchange {
          background: linear-gradient(135deg, #909399, #b3b6bb);
        }

        .preview-value {
          .value-number {
            font-size: 28px;
            font-weight: 700;
          }
        }

        .preview-name {
          font-size: 11px;
          margin-top: 6px;
        }
      }

      .preview-info {
        font-size: 13px;
        color: #666;

        div {
          margin-bottom: 6px;
        }
      }
    }
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .coupon-code {
    font-family: monospace;
    font-size: 13px;
  }

  .pagination-small {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}
</style>